// @spec GHG-STR-006
/**
 * API client functions for GHG settings
 */

import type { GHGSettingsResponse, UpdateGHGSettingsRequest } from '../types/ghg-settings';

import { createApiClient } from '@/lib/api-client';

/**
 * Fetch GHG settings for an organization.
 *
 * @param getToken - Function to get auth token from Clerk
 * @param organizationId - The organization UUID
 * @returns GHG settings response
 */
export async function fetchGHGSettings(
  getToken: () => Promise<string | null>,
  organizationId: string
): Promise<GHGSettingsResponse> {
  const apiClient = createApiClient({
    getAuthToken: getToken,
  });

  return apiClient.get<GHGSettingsResponse>(`/organizations/${organizationId}/ghg/settings`);
}

/**
 * Update GHG settings for an organization.
 *
 * @param getToken - Function to get auth token from Clerk
 * @param organizationId - The organization UUID
 * @param data - Settings update data
 * @returns Updated GHG settings
 */
export async function updateGHGSettings(
  getToken: () => Promise<string | null>,
  organizationId: string,
  data: UpdateGHGSettingsRequest
): Promise<GHGSettingsResponse> {
  const apiClient = createApiClient({
    getAuthToken: getToken,
  });

  return apiClient.put<GHGSettingsResponse>(`/organizations/${organizationId}/ghg/settings`, data);
}

/**
 * Fetch persisted GHG setup wizard state.
 */
export async function fetchSetupWizardState(
  getToken: () => Promise<string | null>,
  organizationId: string
): Promise<Record<string, unknown> | null> {
  const apiClient = createApiClient({
    getAuthToken: getToken,
  });

  return apiClient.get<Record<string, unknown> | null>(
    `/organizations/${organizationId}/ghg/setup-wizard-state`
  );
}

/**
 * Save GHG setup wizard state to the backend.
 */
export async function saveSetupWizardState(
  getToken: () => Promise<string | null>,
  organizationId: string,
  state: Record<string, unknown>
): Promise<{ status: string }> {
  const apiClient = createApiClient({
    getAuthToken: getToken,
  });

  return apiClient.put<{ status: string }>(
    `/organizations/${organizationId}/ghg/setup-wizard-state`,
    { state }
  );
}
